import {
  combineReducers,
  configureStore,
  UnknownAction,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query/react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import {
  authApi,
  authMiddleware,
  posthogMiddleware,
  authSlice,
  clientApi,
  clientSlice,
  documentApi,
  documentSlice,
  spOwnerPartnerSlice,
  spOwnerPartnerApi,
  tradeDataApi,
  tradeDataSlice,
  userManagementApi,
  userManagementSlice,
  signedUrlApi,
  settingApi,
  dashboardApi,
  dashboardSlice,
  settingsSlice,
  reportsApi,
  pollingSlice,
  tenantManagementApi,
  tenantManagementSlice,
  manufacturerSubmissionApi,
  htsDescriptionsApi,
  pageAccessApi,
  systemSettingsApi,
  validationCheckDefinitionsApi,
  chatApi,
  chatAttachmentApi,
  censusTradeDataApi,
  customsDocumentApi,
  refundsAnalyticsApi,
  firstSaleApi,
  firstSalePortalApi,
} from "@/services";
import { selectionMiddleware } from "@/services/selection/selection-middleware";
import { scenarioModelingApi } from "@/services/dashboard/sm-api";
import { productCatalogueApi } from "@/services/product-catalogue/pc-api";
import { dutyColumnsApi } from "@/services/product-catalogue/duty-columns-api";
import { exclusionListsApi } from "@/services/product-catalogue/exclusion-lists-api";
import { metalPercentageApi } from "@/services/product-catalogue/metal-percentage-api";
import { certificateApi } from "@/services/product-certificate/certificate-api";

const appReducer = combineReducers({
  // APIS
  [signedUrlApi.reducerPath]: signedUrlApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [documentApi.reducerPath]: documentApi.reducer,
  [clientApi.reducerPath]: clientApi.reducer,
  [tradeDataApi.reducerPath]: tradeDataApi.reducer,
  [censusTradeDataApi.reducerPath]: censusTradeDataApi.reducer,
  [userManagementApi.reducerPath]: userManagementApi.reducer,
  [spOwnerPartnerApi.reducerPath]: spOwnerPartnerApi.reducer,
  [settingApi.reducerPath]: settingApi.reducer,
  [manufacturerSubmissionApi.reducerPath]: manufacturerSubmissionApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [scenarioModelingApi.reducerPath]: scenarioModelingApi.reducer,
  [tenantManagementApi.reducerPath]: tenantManagementApi.reducer,
  [productCatalogueApi.reducerPath]: productCatalogueApi.reducer,
  [dutyColumnsApi.reducerPath]: dutyColumnsApi.reducer,
  [exclusionListsApi.reducerPath]: exclusionListsApi.reducer,
  [metalPercentageApi.reducerPath]: metalPercentageApi.reducer,
  [certificateApi.reducerPath]: certificateApi.reducer,
  [htsDescriptionsApi.reducerPath]: htsDescriptionsApi.reducer,
  [pageAccessApi.reducerPath]: pageAccessApi.reducer,
  [systemSettingsApi.reducerPath]: systemSettingsApi.reducer,
  [validationCheckDefinitionsApi.reducerPath]: validationCheckDefinitionsApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [chatAttachmentApi.reducerPath]: chatAttachmentApi.reducer,
  [customsDocumentApi.reducerPath]: customsDocumentApi.reducer,
  [refundsAnalyticsApi.reducerPath]: refundsAnalyticsApi.reducer,
  [firstSaleApi.reducerPath]: firstSaleApi.reducer,
  [firstSalePortalApi.reducerPath]: firstSalePortalApi.reducer,

  // SLICES
  [authSlice.name]: authSlice.reducer,
  [documentSlice.name]: documentApi.reducer,
  [clientSlice.reducerPath]: clientSlice.reducer,
  [tradeDataSlice.name]: tradeDataSlice.reducer,
  [userManagementSlice.name]: userManagementSlice.reducer,
  [spOwnerPartnerSlice.name]: spOwnerPartnerSlice.reducer,
  [dashboardSlice.name]: dashboardSlice.reducer,
  [settingsSlice.name]: settingsSlice.reducer,
  [pollingSlice.name]: pollingSlice.reducer,
  [tenantManagementSlice.name]: tenantManagementSlice.reducer,
});

export const store = configureStore({
  reducer: (
    state: ReturnType<typeof appReducer> | undefined,
    action: UnknownAction
  ) => {
    if (action.type === "auth/logout") {
      state = undefined;
    }
    return appReducer(state, action);
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      authMiddleware,
      posthogMiddleware,
      selectionMiddleware,
      signedUrlApi.middleware,
      authApi.middleware,
      documentApi.middleware,
      clientApi.middleware,
      tradeDataApi.middleware,
      censusTradeDataApi.middleware,
      userManagementApi.middleware,
      spOwnerPartnerApi.middleware,
      settingApi.middleware,
      manufacturerSubmissionApi.middleware,
      dashboardApi.middleware,
      reportsApi.middleware,
      scenarioModelingApi.middleware,
      tenantManagementApi.middleware,
      productCatalogueApi.middleware,
      dutyColumnsApi.middleware,
      exclusionListsApi.middleware,
      metalPercentageApi.middleware,
      certificateApi.middleware,
      htsDescriptionsApi.middleware,
      pageAccessApi.middleware,
      systemSettingsApi.middleware,
      validationCheckDefinitionsApi.middleware,
      chatApi.middleware,
      chatAttachmentApi.middleware,
      customsDocumentApi.middleware,
      refundsAnalyticsApi.middleware,
      firstSaleApi.middleware,
      firstSalePortalApi.middleware,
    ]),
  devTools: import.meta.env.DEV,
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = () => useDispatch<AppDispatch>();
